"use client";

import { useState } from "react";
import Link from "next/link";
import dynamic from "next/dynamic";

import BirthDetailsForm from "@/components/BirthDetailsForm";
import OfferImpression from "@/components/OfferImpression";
import { trackCalculatorUsed } from "@/lib/analytics-free-tools";
import RatingPrompt from "@/components/RatingPrompt";
import { RESULT_REASONS } from "@/lib/feedback/resultReasons";
import { reportOfferCopyFor } from "@/lib/report-offer-copy";
import { buildUpayReveal, syntheticDosha, upayUnlockCtaLabel } from "@/lib/upay-reveal";
import {
  checkKaalSarpDosh,
  KAAL_SARP_SUBTYPE_LABELS,
  type KaalSarpResponse,
} from "@/lib/api/kaal-sarp";
import type { PersonInput } from "@ishvaram/api-client";

const PaidReportCTA = dynamic(() => import("@/components/PaidReportCTA"), {
  ssr: false,
  loading: () => null,
});

type BirthDetails = {
  name: string;
  birth_date: string;
  birth_place: string;
  birth_time?: string | null;
};

export default function KaalSarpDoshClient() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<KaalSarpResponse | null>(null);
  const [birth, setBirth] = useState<BirthDetails | null>(null);

  async function handleSubmit(person: PersonInput) {
    setLoading(true);
    setError(null);
    setResult(null);

    trackCalculatorUsed({ calculator: "kaal-sarp-dosh" });

    try {
      const res = await checkKaalSarpDosh({
        name: person.name,
        birth_date: person.birth_date,
        birth_time: person.birth_time ?? "12:00",
        birth_place: person.birth_place,
        latitude: person.latitude ?? 0,
        longitude: person.longitude ?? 0,
        timezone: person.timezone ?? "Asia/Kolkata",
      });
      setResult(res);
      setBirth({
        name: person.name,
        birth_date: person.birth_date,
        birth_place: person.birth_place,
        birth_time: person.birth_time ?? null,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Calculation failed. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="space-y-6">
      {!result && (
        <div className="max-w-md mx-auto">
          <BirthDetailsForm
            onSubmit={handleSubmit}
            loading={loading}
            submitLabel="Check Kaal Sarp Dosh"
            heading="Your birth details"
            analyticsToolName="kaal-sarp-dosh"
            showTrustStrip
          />
        </div>
      )}

      {error && (
        <div
          role="alert"
          className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 max-w-md mx-auto"
        >
          {error}
        </div>
      )}

      {result && (
        <KaalSarpResultPanel
          result={result}
          birth={birth}
          onReset={() => {
            setResult(null);
            setError(null);
            setBirth(null);
          }}
        />
      )}
    </div>
  );
}

function KaalSarpResultPanel({
  result,
  birth,
  onReset,
}: {
  result: KaalSarpResponse;
  birth: BirthDetails | null;
  onReset: () => void;
}) {
  return (
    <div className="space-y-4 max-w-md mx-auto">
      {/* Result card */}
      <div
        className={`rounded-2xl border p-6 ${
          result.is_present ? "border-amber-300 bg-amber-50" : "border-green-300 bg-green-50"
        }`}
      >
        <div className="flex items-start gap-4">
          <span className="text-3xl" aria-hidden="true">
            {result.is_present ? "🐍" : "✅"}
          </span>
          <div>
            <h2 className="font-bold text-lg">
              {result.is_present ? "Kaal Sarp Dosh Detected" : "No Kaal Sarp Dosh"}
            </h2>
            {result.is_present && result.sub_type && (
              <p className="mt-1 text-sm font-medium text-amber-800">
                {/* inert-by-design: sub_type label is descriptive, not a drilldown target */}
                {KAAL_SARP_SUBTYPE_LABELS[result.sub_type]}
              </p>
            )}
            {result.is_present && (
              <p className="mt-2 text-sm text-muted-foreground">
                All seven planets are hemmed between your Rahu and Ketu axis.
                {result.cancellation_applied && result.cancellation_reason && (
                  <>
                    {" "}
                    A cancellation factor applies:{" "}
                    <span className="font-medium">{result.cancellation_reason}</span>.
                  </>
                )}
              </p>
            )}
            {!result.is_present && (
              <p className="mt-2 text-sm text-muted-foreground">
                At least one planet falls outside the Rahu-Ketu arc in your chart. Kaal Sarp Dosh is
                not formed.
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Severity indicator (only when present) */}
      {result.is_present && (
        <div className="rounded-xl border border-border bg-card px-5 py-4">
          <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">
            Severity
          </p>
          {/* inert-by-design: severity bar is a visual indicator, Explainable sheet is on the upay CTA below */}
          <div className="h-2 rounded-full bg-muted overflow-hidden">
            <div
              className="h-full rounded-full bg-amber-500 transition-all"
              style={{ width: `${Math.round(result.severity * 100)}%` }}
            />
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            {Math.round(result.severity * 100)}% — classical severity as per doshas.yaml rules
          </p>
        </div>
      )}

      {/* Upay CTA */}
      {result.is_present && (
        <div className="rounded-xl border border-border bg-card px-5 py-4 space-y-3">
          <p className="text-sm font-semibold">
            Why does this pattern keep repeating in your life?
          </p>
          <p className="text-sm text-muted-foreground">
            Kaal Sarp Dosh creates a ceiling effect — your efforts produce results, but never quite
            the breakthrough you are working toward. The classical upay addresses the Rahu-Ketu axis
            directly.
          </p>
          <Link
            href="/kaal-sarp-dosha/upay/"
            className="inline-block rounded-lg bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground hover:opacity-90 transition-opacity"
          >
            See Classical Upay →
          </Link>
        </div>
      )}

      {/* Intent-matched report offer — rule 46: offer travels with the value moment */}
      <KaalSarpReportOffer result={result} birth={birth} />

      <div className="mt-6">
        <RatingPrompt
          source={"tool_result" as never}
          mode="thumbs+reasons"
          reasons={RESULT_REASONS}
          prompt="Was this Kaal Sarp Dosh check accurate?"
          context={{
            surface: "kaal_sarp_dosh_result",
            path: typeof window !== "undefined" ? window.location.pathname : null,
          }}
        />
      </div>

      {/* Check another */}
      <button
        onClick={onReset}
        className="w-full text-sm text-muted-foreground underline underline-offset-2"
      >
        Check another chart
      </button>
    </div>
  );
}

function KaalSarpReportOffer({
  result,
  birth,
}: {
  result: KaalSarpResponse;
  birth: BirthDetails | null;
}) {
  const sourcePath = "/kaal-sarp-dosh";
  const planId = "dosha_remedy_plan";
  const placement = "kaal_sarp_result_primary";
  if (!birth) return null;
  const planCopy = reportOfferCopyFor(planId, "en");
  // Kaal Sarp is computed outside the standard Dosha[] array, so wrap its result
  // in a synthetic dosha to lead with the pain question + one free upay when present.
  const reveal = result.is_present
    ? buildUpayReveal(
        syntheticDosha({
          doshaId: "kaal_sarp_dosh",
          nameEn: "Kaal Sarp Dosh",
          nameHi: "Kaal Sarp Dosh",
          isPresent: true,
          severity: result.severity ?? 0.6,
        }),
        "en"
      )
    : null;
  return (
    <>
      <OfferImpression
        planId={planId}
        sourcePath={sourcePath}
        intent="dosha_remedy"
        placement={placement}
        customerProblem="kaal_sarp_dosh"
        urgency={result.is_present ? "high" : "medium"}
        confidence={0.88}
        routeRankScore={88}
        routeRankReason="kaal_sarp_result"
      />
      <PaidReportCTA
        planId={planId}
        priceInr={planCopy.priceInr}
        headline={
          reveal
            ? reveal.painHeadline
            : result.is_present
              ? "Get your Kaal Sarp Dosh Report — severity, upay & timing"
              : "Get your full Dosha Report — all 9 doshas checked"
        }
        doshaReveal={
          reveal
            ? {
                freeUpayTitle: reveal.freeUpayTitle,
                freeUpayWhy: reveal.freeUpayWhy,
                ctaLabel: upayUnlockCtaLabel(planCopy.priceInr, "en"),
              }
            : undefined
        }
        subhead={
          result.is_present
            ? "18-page PDF — Kaal Sarp type, severity, Rahu-Ketu axis remedies, dasha activation timing, and personalised upay."
            : "18-page PDF — detailed analysis of all 9 doshas, personalised upay, dasha predictions, and remedies."
        }
        language="en"
        source="kaal-sarp-dosh"
        analyticsContext={{
          placement,
          checkoutChannel: "ishvaram_report",
          commerceSystem: "ishvaram",
          intent: "dosha_remedy",
          sourcePath,
          customerProblem: "kaal_sarp_dosh",
          urgency: result.is_present ? "high" : "medium",
          confidence: 0.88,
          routeRankScore: 88,
          routeRankReason: "kaal_sarp_result",
          rankedOfferOrder: [planId],
        }}
        reportInputs={{
          name: birth.name,
          birth_date: birth.birth_date,
          birth_time: birth.birth_time ?? null,
          birth_place: birth.birth_place,
          language: "en",
        }}
      />
    </>
  );
}
